import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';

import {
  AiPersonalizationSection,
  AiPreferencesSection,
  AudioSettingsSection,
  DeleteAccountSection,
  ProfileSettingsSection,
  SecuritySettingsSection,
  AppearanceSection,
  Button,
  LanguageDefaultsSection,
  MeetingDefaultsSection,
  Modal,
  SettingsMenu,
  SubscriptionSummary,
} from '@copilot/ui';
import Icon from '../components/common/Icon';
import { LEGAL_DOCUMENTS, LegalDocumentBody } from './LegalPage';
import { getUserFriendlyError } from '../utils/errors';
import { API_BASE_URL } from '../services/httpClient';
import { FIXED_AUDIO_SOURCE_LABEL } from '../constants/audioSources';
import { CO_PILOT_MODES, LANGUAGES } from '../constants/languages';
import { useAuth } from '../hooks/useAuth';
import { useAiPersonalizationApi } from '../hooks/useAiPersonalizationApi';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useBrowserMicrophone } from '../hooks/useBrowserMicrophone';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useSettingsApi } from '../hooks/useSettingsApi';
import { useTheme } from '../hooks/useTheme';
import * as authApi from '../services/authApi';
import * as pricingApi from '../services/pricingApi';

// The browser's microphone permission (Permissions API), in words.
const MIC_PERMISSION_BADGES = {
  granted: { tone: 'success', label: 'Permission granted' },
  denied: { tone: 'danger', label: 'Permission blocked' },
  prompt: { tone: 'accent', label: 'Permission required' },
  checking: { tone: 'neutral', label: 'Checking permission' },
  unknown: { tone: 'neutral', label: 'Permission unknown' },
};

// Keep in step with layout.css (.settings-workspace / .settings-mobile-accordion).
const SETTINGS_ACCORDION_QUERY = '(max-width: 1024px)';

const SETTINGS_NAV_GROUPS = [
  {
    label: 'General',
    items: [
      { key: 'profile', label: 'Profile', mobileLabel: 'Profile', icon: 'user-round' },
      {
        key: 'ai-behavior',
        label: 'AI Personalization',
        mobileLabel: 'AI Personalization',
        icon: 'sparkles',
      },
    ],
  },
  {
    label: 'AI',
    items: [
      { key: 'language', label: 'Language', mobileLabel: 'Language', icon: 'languages' },
      {
        key: 'meeting-defaults',
        label: 'Meeting Defaults',
        mobileLabel: 'Meeting Settings',
        icon: 'meeting',
      },
    ],
  },
  {
    label: 'Audio',
    items: [{ key: 'audio', label: 'Microphone', mobileLabel: 'Audio', icon: 'mic' }],
  },
  {
    label: 'Appearance',
    items: [{ key: 'appearance', label: 'Theme', mobileLabel: 'Appearance', icon: 'palette' }],
  },
  {
    label: 'Security',
    items: [
      {
        key: 'security',
        label: 'Password & Sessions',
        mobileLabel: 'Security',
        icon: 'shield',
      },
    ],
  },
  {
    // These open the document in a pop-up panel over Settings instead of
    // switching panel - `legal` is what handleSettingsMenuSelect branches on. They exist because the
    // policies were previously reachable only from the landing footer,
    // the signup page and the one-time consent prompt: once someone had
    // accepted, there was nowhere in the product to read them again.
    // Both the DPDP Act and the GDPR expect the policy to be available,
    // not shown once and withdrawn.
    label: 'Legal',
    items: [
      {
        key: 'privacy-policy',
        label: 'Privacy Policy',
        mobileLabel: 'Privacy',
        icon: 'shield',
        legal: 'privacy',
      },
      {
        key: 'terms-of-service',
        label: 'Terms of Service',
        mobileLabel: 'Terms',
        icon: 'file-text',
        legal: 'terms',
      },
    ],
  },
];

function formatRenewalDate(isoString) {
  if (!isoString) return '—';

  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// The Profile tab's plan summary. Deliberately just a summary with ONE
// action - full plan management (features, payment history, plan
// selection, cancellation) lives on Plan & Billing, not duplicated here,
// so this card can never drift out of sync with it. Presentation comes
// from the shared @copilot/ui SubscriptionSummary (same component
// Desktop's SubscriptionSummaryCard renders) - this wrapper only
// supplies the data and the navigation callback.
function SubscriptionSummaryCard({ subscription, onManageBilling }) {
  // Same status/date rule the Plan & Billing page uses (services/
  // pricingApi.js's getSubscriptionPresentation) - a single shared
  // helper so this card can never say something Plan & Billing
  // contradicts.
  const presentation = pricingApi.getSubscriptionPresentation(subscription);

  return (
    <SubscriptionSummary
      planName={subscription.plan.name}
      statusTone={presentation?.statusTone ?? 'neutral'}
      statusLabel={presentation?.statusLabel ?? 'Free plan'}
      dateLabel={presentation?.dateLabel ?? 'Renews'}
      dateValue={formatRenewalDate(subscription.end_date)}
      pastDueNotice={
        presentation?.isPastDue && (
          <>
            We couldn&apos;t process your latest payment.{' '}
            <Link to="/app/billing">Fix it from Plan &amp; Billing</Link>.
          </>
        )
      }
      onManageBilling={onManageBilling}
    />
  );
}

function SettingsPage() {
  const { user, updateUser, logout } = useAuth();
  const { theme, followsSystem, setTheme, followSystemTheme } = useTheme();
  const navigate = useNavigate();
  const settingsApi = useSettingsApi();
  const aiPersonalizationApi = useAiPersonalizationApi();
  // Reported by AI Personalization; shapes how Additional AI Settings
  // presents the legacy answer controls.
  const [aiPersonalizationStatus, setAiPersonalizationStatus] = useState('unknown');
  const microphone = useBrowserMicrophone();
  const [microphoneSaveError, setMicrophoneSaveError] = useState('');
  const selectedMicrophoneId = user?.preferences?.default_microphone_id || '';
  const microphoneOptions = microphone.microphones
    .filter((device) => device.deviceId)
    .map((device, index) => ({
      id: device.deviceId,
      label: device.label || `Microphone ${index + 1}`,
    }));

  const selectedMicrophoneLabel =
    microphoneOptions.find((option) => option.id === selectedMicrophoneId)?.label ||
    'System default microphone';

  // A device choice applies at once, like every other Settings control
  // that is not a form - PATCH /api/users/me/preferences merges.
  const saveMicrophone = async (deviceId) => {
    setMicrophoneSaveError('');
    try {
      await settingsApi.updatePreferences({ default_microphone_id: deviceId || '' });
    } catch (err) {
      setMicrophoneSaveError(
        getUserFriendlyError(err, 'Unable to save the microphone. Please try again.')
      );
    }
  };

  const [activeSettingsSection, setActiveSettingsSection] = useState('profile');
  // The same breakpoint as layout.css's accordion. The selected section is
  // rendered ONCE - never a copy per layout: two hidden-but-mounted copies of
  // a form meant duplicate ids (labels pointing at the other copy), separate
  // unsaved drafts and every request made twice.
  const compactLayout = useMediaQuery(SETTINGS_ACCORDION_QUERY);
  // That one copy is portalled into a node that lives as long as the page;
  // a layout change only MOVES the node between the accordion and the side
  // panel. Moving DOM never remounts React, so unsaved edits, an open editor
  // and loaded data survive crossing the breakpoint.
  const [sectionHost] = useState(() => {
    const node = document.createElement('div');
    node.className = 'settings-section-host';
    return node;
  });
  const [sectionSlot, setSectionSlot] = useState(null);
  useLayoutEffect(() => {
    if (!sectionSlot || sectionHost.parentNode === sectionSlot) return;
    // Moving a node blurs whatever inside it had focus; put it back. Both
    // slots stay mounted across a layout change (below), so the field is
    // still focused - and found - right up to the move.
    const focused = sectionHost.contains(document.activeElement) ? document.activeElement : null;
    sectionSlot.appendChild(sectionHost);
    if (focused && document.activeElement !== focused) focused.focus({ preventScroll: true });
  }, [sectionSlot, sectionHost]);
  // 'privacy' | 'terms' while that document's pop-up panel is open.
  const [legalDocument, setLegalDocument] = useState(null);

  // Plan details for the Profile tab's Subscription card. The Usage
  // meters that used to sit here moved to their own top-level Workspace
  // page (pages/UsagePage.jsx); this still hits the same
  // GET /api/subscriptions/me for the plan name / features / renewal.
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    let cancelled = false;

    pricingApi
      .getMySubscription()
      .then((usageData) => {
        if (!cancelled) setUsage(usageData);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  // Silent refresh of the read-only plan data only - never the
  // preference forms, so unsaved edits are untouched.
  const refreshReadOnlyData = useCallback(async ({ isStale }) => {
    const usageData = await pricingApi.getMySubscription();
    if (isStale()) return;
    setUsage(usageData);
  }, []);

  useAutoRefresh(refreshReadOnlyData);


  const activeSettingsItem = SETTINGS_NAV_GROUPS
    .flatMap((group) => group.items)
    .find((item) => item.key === activeSettingsSection);
  const settingsNavItems = SETTINGS_NAV_GROUPS.flatMap((group) => group.items);
  const settingsMenuSections = SETTINGS_NAV_GROUPS.map((group) => ({
    label: group.label,
    items: group.items.map((item) => ({
      key: item.key,
      label: item.label,
      icon: <Icon name={item.icon} size={16} />,
    })),
  }));
  const handleSettingsMenuSelect = (key) => {
    const item = settingsNavItems.find((navItem) => navItem.key === key);
    if (item?.legal) {
      setLegalDocument(item.legal);
      return;
    }
    if (item?.to) {
      navigate(item.to);
      return;
    }
    setActiveSettingsSection(key);
  };




  const renderSettingsSection = () => {
    switch (activeSettingsSection) {
      case 'profile':
        return (
          <ProfileSettingsSection
            user={user}
            avatarSrc={user?.avatar_url ? `${API_BASE_URL}${user.avatar_url}` : undefined}
            languages={LANGUAGES}
            avatarPicker="file"
            onUploadAvatar={async (file) => updateUser(await authApi.uploadAvatar(file))}
            onSaveProfile={async (profile) => updateUser(await authApi.updateProfile(profile))}
            describeError={getUserFriendlyError}
          >
            {usage?.subscription && (
              <SubscriptionSummaryCard
                subscription={usage.subscription}
                onManageBilling={() => navigate('/app/billing')}
              />
            )}
          </ProfileSettingsSection>
        );

      case 'ai-behavior':
        return (
          <>
            <AiPersonalizationSection
              api={aiPersonalizationApi}
              accountId={user?.id}
              onStatusChange={setAiPersonalizationStatus}
            />
            {/* The operational AI settings, plus the legacy answer controls
                for meetings without an AI Response Style. */}
            <AiPreferencesSection
              api={settingsApi}
              preferences={user?.preferences}
              personalization={aiPersonalizationStatus}
            />
          </>
        );

      case 'language':
        return (
          <LanguageDefaultsSection
            api={settingsApi}
            preferences={user?.preferences}
            languages={LANGUAGES}
          />
        );

      case 'meeting-defaults':
        return (
          <MeetingDefaultsSection
            api={settingsApi}
            preferences={user?.preferences}
            modes={CO_PILOT_MODES}
            audioSourceLabel={FIXED_AUDIO_SOURCE_LABEL}
          />
        );

      case 'audio':
        return (
          <AudioSettingsSection
            permission={MIC_PERMISSION_BADGES[microphone.permission] || MIC_PERMISSION_BADGES.unknown}
            alerts={
              (microphone.error || microphoneSaveError) && (
                <div className="form-alert" role="alert">
                  {microphone.error || microphoneSaveError}
                </div>
              )
            }
            sources={[
              {
                key: 'microphone',
                title: 'Microphone',
                icon: 'mic',
                selectedLabel: selectedMicrophoneLabel,
                help: 'KrishvaQ hears you through this browser. Speaker output follows your browser and system settings.',
                devices: microphoneOptions,
                selectedDeviceId: selectedMicrophoneId || null,
                onSelectDevice: (deviceId) => void saveMicrophone(deviceId),
                level: microphone.testing ? microphone.level : null,
              },
            ]}
            actions={
              <Button
                variant="secondary"
                onClick={() =>
                  microphone.testing
                    ? microphone.stopTest()
                    : microphone.startTest(selectedMicrophoneId)
                }
              >
                {microphone.testing ? 'Stop Microphone Test' : 'Test Microphone'}
              </Button>
            }
          />
        );

      case 'appearance':
        return (
          <AppearanceSection
            theme={theme}
            followsSystem={followsSystem}
            onSelect={(choice) =>
              choice === 'system' ? followSystemTheme() : setTheme(choice)
            }
          />
        );

      case 'security':
      default:
        return (
          <SecuritySettingsSection
            hasPassword={user?.has_password !== false}
            onChangePassword={(oldPassword, newPassword) =>
              authApi.changePassword({ oldPassword, newPassword })
            }
            onLogoutAllDevices={async () => {
              // Invalidates every other session server-side, then ends
              // this one too, so the page never looks signed in after.
              await authApi.logoutAllDevices();
              await logout();
              navigate('/login', { replace: true });
            }}
            describeError={getUserFriendlyError}
          >
            <DeleteAccountSection
              email={user?.email || ''}
              hasPassword={user?.has_password !== false}
              onDeleteAccount={async (confirmation) => {
                await authApi.deleteAccount(confirmation);
                // The server already revoked every session; clear the
                // client state so nothing renders as signed in on the way out.
                await logout();
                navigate('/', { replace: true });
              }}
              describeError={getUserFriendlyError}
            />
          </SecuritySettingsSection>
        );
    }
  };

  return (
    <div className="settings-page">
      <header className="settings-page-header">
        <div className="settings-page-heading">
          <span className="settings-page-icon">
            <Icon name="settings" size={20} />
          </span>
          <div>
            <h1>Settings</h1>
            <p>
              Manage your preferences, AI behavior, language and account
              security.
            </p>
          </div>
        </div>

      </header>


      <div className="settings-mobile-accordion" aria-label="Settings sections">
        {settingsNavItems.map((item) => {
          if (item.legal) {
            return (
              <div className="settings-mobile-panel" key={item.key}>
                <button
                  type="button"
                  className="settings-mobile-trigger"
                  onClick={() => setLegalDocument(item.legal)}
                >
                  <span>
                    <Icon name={item.icon} size={18} />
                    {item.mobileLabel || item.label}
                  </span>
                </button>
              </div>
            );
          }

          if (item.to) {
            return (
              <div className="settings-mobile-panel" key={item.key}>
                <Link to={item.to} className="settings-mobile-trigger">
                  <span>
                    <Icon name={item.icon} size={18} />
                    {item.mobileLabel || item.label}
                  </span>
                </Link>
              </div>
            );
          }

          const isActive = activeSettingsSection === item.key;

          return (
            <div
              className={`settings-mobile-panel ${isActive ? 'active' : ''}`}
              key={item.key}
            >
              <button
                type="button"
                className="settings-mobile-trigger"
                aria-expanded={isActive}
                onClick={() => setActiveSettingsSection(item.key)}
              >
                <span>
                  <Icon name={item.icon} size={18} />
                  {item.mobileLabel || item.label}
                </span>
                <Icon name="chevron-down" size={18} />
              </button>

              {/* Rendered at every width (the accordion is hidden on wide
                  screens) so the section never loses its parent mid-move. */}
              {isActive && (
                <div
                  className="settings-mobile-content"
                  ref={compactLayout ? setSectionSlot : undefined}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="settings-workspace">
        <SettingsMenu
          sections={settingsMenuSections}
          activeSection={activeSettingsSection}
          onSelect={handleSettingsMenuSelect}
        />

        <section
          className="settings-detail"
          aria-label={activeSettingsItem?.label || 'Selected settings'}
          ref={compactLayout ? undefined : setSectionSlot}
        />
      </div>

      {createPortal(renderSettingsSection(), sectionHost)}

      {legalDocument && (
        <Modal
          size="lg"
          title={LEGAL_DOCUMENTS[legalDocument].title}
          onClose={() => setLegalDocument(null)}
          actions={
            <Button variant="secondary" onClick={() => setLegalDocument(null)}>
              Close
            </Button>
          }
        >
          <LegalDocumentBody doc={LEGAL_DOCUMENTS[legalDocument]} inModal />
        </Modal>
      )}
    </div>
  );
}

export default SettingsPage;
